import { ApplicationContextManager, ViewComponent } from './checkpointHelper/static/appfactory3';
import { initTheme, toggleTheme } from './theme';
import { mountEditor } from './editor';
import { initDashboard } from './dashboard';
import { showToast } from './toast';

import shellTemplate from './templates/shell.html?raw';
import dashboardTemplate from './templates/dashboard.html?raw';
import editorTemplate from './templates/editor.html?raw';
import settingsTemplate from './templates/settings.html?raw';
import { initSettingsView } from './settings/init';
import { updateUserAccountNav } from './auth-display';

export let view: ViewComponent;

const CONTENT_CONTAINER = 'app-content';

/** Renders a registered subview into the shell's content container. */
export function navigateTo(app: ApplicationContextManager, id: string, params: any = {}): void {
    view.render(id, params, { container: CONTENT_CONTAINER });
}

function setupNav(app: ApplicationContextManager): void {
    document.querySelectorAll('[data-nav]').forEach((link) => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const target = (e.currentTarget as HTMLElement).getAttribute('data-nav');
            if (target) navigateTo(app, target);
        });
    });
}

/**
 * Resolves a `#/shared?token=...` link: fetches the share's document metadata
 * and bytes (no ownership needed — see DocumentShareController::openByToken
 * and DocumentController::resolveAccess), then hands off to the normal editor
 * view exactly like opening one of your own documents.
 */
async function openSharedLink(app: ApplicationContextManager, token: string | undefined): Promise<void> {
    if (!token) {
        navigateTo(app, 'dashboard');
        showToast('Missing share link token');
        return;
    }
    try {
        const metaRes = await fetch(`/api/shared/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } });
        if (!metaRes.ok) throw new Error(String(metaRes.status));
        const meta = (await metaRes.json()) as { id: number; name: string; permission: 'view' | 'edit' };

        const fileRes = await fetch(`/api/documents/${meta.id}/file?token=${encodeURIComponent(token)}`);
        if (!fileRes.ok) throw new Error(String(fileRes.status));
        const bytes = new Uint8Array(await fileRes.arrayBuffer());

        navigateTo(app, 'editor', {
            action: 'open-bytes',
            bytes,
            name: meta.name,
            docId: meta.id,
            shareToken: token,
            sharePermission: meta.permission,
        });
    } catch (err) {
        console.error(err);
        showToast('This share link is invalid or has been revoked');
        navigateTo(app, 'dashboard');
    }
}

export function registerViews(app: ApplicationContextManager): void {
    view = app.factory.view();

    // Persistent shell: navbar + theme toggle + the container routed subviews render into.
    view.newSubView({
        id: 'main',
        init: true,
        body: shellTemplate,
        listener: function (params: any, appCtx: ApplicationContextManager) {
            setupNav(appCtx);
            document.getElementById('btn-theme')?.addEventListener('click', toggleTheme);
            initTheme();
            updateUserAccountNav();
            navigateTo(appCtx, 'dashboard');
        },
    });

    // Dashboard: lists saved PDFs and lets the user start or open one.
    view.newSubView({
        id: 'dashboard',
        route: '/',
        router: { useHash: true },
        body: dashboardTemplate,
        renderOptions: { container: CONTENT_CONTAINER },
        listener: function (params: any, appCtx: ApplicationContextManager) {
            // Dashboard uses the regular top navbar.
            document.getElementById('app-shell')?.classList.remove('app-shell--side');
            initDashboard(appCtx);
        },
    });

    // Editor: the PDF canvas/toolbar. All PDF editing logic lives in editor.ts, untouched.
    view.newSubView({
        id: 'editor',
        route: '/editor',
        body: editorTemplate,
        renderOptions: { container: CONTENT_CONTAINER },
        listener: function (params: any) {
            // Collapse the navbar into a left rail so the PDF gets the full
            // viewport height while editing.
            document.getElementById('app-shell')?.classList.add('app-shell--side');
            mountEditor(params);
        },
    });

    // Shared link: #/shared?token=... — resolves the token then hands off to
    // the editor view above. No template of its own beyond a brief spinner.
    view.newSubView({
        id: 'shared-link',
        route: '/shared',
        body: '<div class="d-flex align-items-center justify-content-center flex-fill py-5"><div class="spinner-border text-primary" role="status"></div></div>',
        renderOptions: { container: CONTENT_CONTAINER },
        listener: function (params: any, appCtx: ApplicationContextManager) {
            document.getElementById('app-shell')?.classList.remove('app-shell--side');
            void openSharedLink(appCtx, params?.token);
        },
    });

    view.newSubView({
        id: 'settings',
        route: '/settings',
        router: { useHash: true },
        body: settingsTemplate,
        renderOptions: { container: CONTENT_CONTAINER },
        listener: function () {
            document.getElementById('app-shell')?.classList.remove('app-shell--side');
            initSettingsView();
        },
    });
}
